const m = require('minthril');
const { component } = require('../../component');

function hostName(name) {
  return name.replace(/\.$/, '');
}

function hostNameParts(name, domain) {
  const hostname = hostName(name);
  const suffix = `.${domain}`;

  if (!hostname.endsWith(suffix)) {
    return { name: hostname, domain: '' };
  }

  return {
    name: hostname.slice(0, -suffix.length),
    domain: suffix
  };
}

function connectionStep(number, title, content, options = {}) {
  const state = options.complete ? 'complete' : options.current ? 'current' : 'pending';

  return m(`li.domain-provisioning__step.domain-provisioning__step--${state}`, [
    m('span.domain-provisioning__step-indicator', options.complete ? '✓' : number),
    m('div.domain-provisioning__step-content', [
      m('h5', title),
      content
    ])
  ]);
}

function certificateStatus(domain) {
  if (domain.status === 'active') {
    return {
      className: 'active',
      text: 'Certificate active. Complete the final DNS cutover below.'
    };
  }

  if (domain.status === 'failed') {
    return {
      className: 'failed',
      text: domain.failureDetails || domain.failureReason || 'Certificate Manager could not validate this domain.'
    };
  }

  if (domain.status === 'issuing') {
    return {
      className: 'issuing',
      text: 'DNS ownership is confirmed. Certificate Manager is issuing the certificate.'
    };
  }

  if (domain.status === 'waiting-for-dns') {
    return {
      className: 'pending',
      text: 'Certificate Manager is checking the DNS verification record.'
    };
  }

  return {
    className: 'pending',
    text: domain.dnsRecord
      ? 'Certificate Manager is preparing to check the DNS verification record.'
      : 'Certificate connection has not been prepared yet.'
  };
}

function dnsTroubleshooting(domain) {
  if (domain.status === 'failed') {
    return {
      title: 'Certificate validation failed',
      items: [
        'Confirm the CNAME name and target above match your DNS provider exactly.',
        'Remove any other CNAME record using the same host name.',
        'After correcting DNS, use Refresh certificate status to ask Google to try again.'
      ]
    };
  }

  if (domain.status === 'waiting-for-dns') {
    return {
      title: 'DNS has not been confirmed yet',
      items: [
        'Some DNS providers append the domain automatically. If so, enter only the host portion shown before the faint suffix.',
        'Keep the CNAME target exactly as shown, including its final dot if your provider supports it.',
        'DNS changes can take several minutes to appear. Refresh certificate status after the record is visible publicly.'
      ]
    };
  }

  if (domain.status === 'issuing' || domain.status === 'checking') {
    return {
      title: 'Certificate issuance is in progress',
      items: [
        'DNS ownership has been found. Certificate issuance can take a few minutes.',
        'Keep the verification CNAME record in place until the certificate is active.',
        'Refresh certificate status if it remains unchanged after DNS has propagated.'
      ]
    };
  }

  return {
    title: 'Need help with the DNS cutover?',
    items: [
      'Point the domain A record at the Spache edge IP shown above.',
      'Keep the _acme-challenge CNAME record in place while the certificate is active.',
      'If the domain still shows the old site, wait for the previous DNS record to expire.'
    ]
  };
}

const DomainProvisioning = component((vnode, emit) => {
  const refreshingDomains = new Set();
  const testingDomains = new Set();
  const testResults = new Map();
  let copiedValue = '';

  async function copyText(value) {
    try {
      await navigator.clipboard.writeText(value);
      copiedValue = value;
      emit('redraw');
      setTimeout(() => {
        if (copiedValue !== value) return;
        copiedValue = '';
        emit('redraw');
      }, 1_500);
    } catch {
      copiedValue = '';
      emit('redraw');
    }
  }

  function copyableCode(value, content = value) {
    return m('span.domain-provisioning__copyable-code', [
      m('code', content),
      m('button.domain-provisioning__copy', {
        type: 'button',
        onclick: () => copyText(value)
      }, copiedValue === value ? 'Copied' : 'Copy')
    ]);
  }

  return {
    view: ({ attrs: {
      site,
      onrefresh,
      ontest,
      isProvisioning,
      isRemoving,
      onRemove
    } }) => {
      const connections = (site.domains || []).map(domain => (
        site.domainProvisioning?.find(item => item.domain === domain) || { domain }
      ));

      async function refreshCertificate(domain) {
        refreshingDomains.add(domain);
        emit('redraw');

        try {
          await onrefresh(domain);
        } finally {
          refreshingDomains.delete(domain);
          emit('redraw');
        }
      }

      async function testDomain(domain) {
        testingDomains.add(domain);
        testResults.delete(domain);
        emit('redraw');

        try {
          const result = await ontest(domain);
          testResults.set(domain, result);
        } catch (reason) {
          testResults.set(domain, { ready: false, error: reason.message });
        } finally {
          testingDomains.delete(domain);
          emit('redraw');
        }
      }

      if (!connections.length) {
        return null;
      }

      return m('div.domain-provisioning',
        connections.map(domain => {
          const refreshing = refreshingDomains.has(domain.domain);
          const domainIsProvisioning = isProvisioning?.(domain.domain);
          const removing = isRemoving?.(domain.domain);
          const certificateActive = domain.status === 'active';
          const dnsConfirmed = domain.authorizationState === 'AUTHORIZED' ||
            domain.authorizationState === 'ACTIVE';
          const certificateFailed = domain.status === 'failed';
          const status = certificateStatus(domain);
          const troubleshooting = dnsTroubleshooting(domain);
          const verificationHost = domain.dnsRecord
            ? hostNameParts(domain.dnsRecord.name, domain.domain)
            : null;
          const refreshingCertificate = refreshing || domainIsProvisioning;
          const testing = testingDomains.has(domain.domain);
          const testResult = testResults.get(domain.domain);

          return m('article.domain-provisioning__connection', [
            m('div.domain-provisioning__connection-header', [
              m('h4',
                m('a', {
                  href: `https://${domain.domain}`,
                  target: '_blank',
                  rel: 'noreferrer'
                }, domain.domain)
              ),
              m('button.domain-provisioning__remove', {
                type: 'button',
                disabled: removing,
                onclick: () => onRemove(domain.domain)
              }, removing ? 'Removing…' : 'Remove')
            ]),
            m(`p.domain-provisioning__status.domain-provisioning__status--${status.className}`,
              status.text
            ),
            m('ol.domain-provisioning__steps', [
              connectionStep('1', 'Add the DNS verification record', domain.dnsRecord
                ? [
                  m('p', `At your DNS provider, add this ${domain.dnsRecord.type} record:`),
                  m('dl.domain-provisioning__record', [
                    m('div', [
                      m('dt', 'Type'),
                      m('dd', copyableCode(domain.dnsRecord.type))
                    ]),
                    m('div', [
                      m('dt', 'Host / Name'),
                      m('dd', copyableCode(verificationHost.name, [
                        verificationHost.name,
                        verificationHost.domain && m('span.domain-provisioning__zone-suffix', {
                          title: 'Your DNS provider already knows this domain. Do not enter this suffix in Shopify.'
                        }, verificationHost.domain)
                      ]))
                    ]),
                    m('div', [
                      m('dt', 'Value / Target'),
                      m('dd', copyableCode(domain.dnsRecord.data))
                    ])
                  ]),
                  m('p', 'Use the Host / Name without its final dot. Keep the final dot in Value / Target.')
                ]
                : m('p', 'Spache is preparing this record.'), {
                complete: dnsConfirmed || certificateActive,
                current: !dnsConfirmed && !certificateActive && !certificateFailed
              }),
              connectionStep('2', 'Wait for the certificate', certificateActive
                ? m('p', 'Certificate Manager has validated this domain and issued its certificate.')
                : certificateFailed
                  ? [
                    m('p', 'Google returned the issue above. After correcting it, refresh this status to check whether Certificate Manager has retried.'),
                    m('button.link-button.domain-provisioning__refresh', {
                      type: 'button',
                      disabled: refreshingCertificate,
                      onclick: () => refreshCertificate(domain.domain)
                    }, [
                      refreshingCertificate && m('span.domain-provisioning__spinner', { 'aria-hidden': 'true' }),
                      refreshingCertificate
                        ? 'Refreshing certificate status…'
                        : 'Refresh certificate status'
                    ])
                  ]
                : [
                  m('p', 'Refresh this status to see whether DNS ownership is confirmed and the certificate is issuing.'),
                  m('button.link-button.domain-provisioning__refresh', {
                    type: 'button',
                    disabled: refreshingCertificate,
                    onclick: () => refreshCertificate(domain.domain)
                  }, [
                    refreshingCertificate && m('span.domain-provisioning__spinner', { 'aria-hidden': 'true' }),
                    refreshingCertificate
                      ? 'Refreshing certificate status…'
                      : 'Refresh certificate status'
                  ])
                ], {
                complete: certificateActive,
                current: !certificateActive && !certificateFailed
              }),
              connectionStep('3', 'Point the domain at Spache', domain.edgeIp
                ? [
                  m('p', [
                    'Replace the existing A record for ',
                    copyableCode(domain.domain),
                    ' with:'
                  ]),
                  m('p', copyableCode(domain.edgeIp)),
                  m('p', 'Keep the _acme-challenge CNAME verification record in place. Then visit this domain over HTTPS to confirm Spache is serving the site.')
                ]
                : m('p', 'The deployed Spache app API has no edge IP configured. This does not affect certificate issuance, but it must be fixed before DNS cutover.'), {
                complete: Boolean(testResult?.ready),
                current: certificateActive && !testResult?.ready
              })
            ]),
            certificateActive && domain.edgeIp && m('div.domain-provisioning__test', [
              m('button.link-button.domain-provisioning__test-button', {
                type: 'button',
                disabled: testing,
                onclick: () => testDomain(domain.domain)
              }, testing ? 'Testing domain…' : 'Test domain'),
              testResult && m('p', {
                class: testResult.ready
                  ? 'domain-provisioning__test-result domain-provisioning__test-result--success'
                  : 'domain-provisioning__test-result domain-provisioning__test-result--failure'
              }, testResult.ready
                ? 'Complete. This domain is serving through Spache.'
                : `Not serving through Spache yet${testResult.error ? `: ${testResult.error}` : '.'}`)
            ]),
            m('details.domain-provisioning__troubleshooting', [
              m('summary', troubleshooting.title),
              m('ul', troubleshooting.items.map(item => m('li', item)))
            ])
          ]);
        })
      );
    }
  };
});

module.exports = { DomainProvisioning };
